import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Info } from "lucide-react";
import { API_URL } from '../config';
import { authenticatedFetch } from '../utils/api';
import InfoPopup from './InfoPopup';
import { vizNarratives } from '../content/vizNarratives';
import { useAskAnalyst } from '../context/AskAnalystContext';

const C = {
  bg: "#E8EDEB", bg2: "#F9FBFA", surface: "#ffffff",
  border: "#C1C7C6", border2: "#C1C7C6", fg: "#1C2D38", muted: "#5C6C75",
  dim: "#3D4F58", blue: "#016BF8", green: "#00A35C", accent: "#00684A",
  red: "#DB3030", amber: "#944F01",
};

const REGIONS = [
  { value: "Global", label: "Global" },
  { value: "APAC", label: "APAC" },
  { value: "EMEA", label: "EMEA" },
  { value: "INDIA", label: "India" },
  { value: "LATAM", label: "LATAM" },
  { value: "North America", label: "North America" },
];

// Fixed 9-channel taxonomy — must match Backend/config.py MEDIA_CHANNELS
const CHANNEL_DISPLAY_ORDER = [
  "Google Brand",
  "Google Non-Brand",
  "Bing Brand",
  "Bing Non-Brand",
  "LinkedIn",
  "Meta",
  "Display",
  "YouTube",
  "All Channels",
];

const normChannel = (n) => (n == null ? "" : String(n)).replace(/_/g, " ");

const quarterKeysFromRow = (side) => {
  if (!side) return [];
  return Object.keys(side)
    .filter((k) => k.endsWith("Budget") && k !== "totalBudget")
    .map((k) => k.slice(0, -"Budget".length));
};

const emptySides = (quarterKeys) => {
  const cur = { totalBudget: 0, totalPts: 0 };
  const opt = { totalBudget: 0, totalPts: 0 };
  quarterKeys.forEach((q) => {
    const b = `${q}Budget`;
    const p = `${q}Pts`;
    cur[b] = 0;
    cur[p] = 0;
    opt[b] = 0;
    opt[p] = 0;
  });
  return { current: cur, optimal: opt };
};

/** Always 9 rows in MEDIA_CHANNELS order; pad missing API channels with zeros. */
const ensureAllChannels = (rows, quarterKeys) => {
  const keys =
    quarterKeys.length > 0
      ? quarterKeys
      : quarterKeysFromRow((rows && rows[0] && rows[0].current) || null);
  const byName = new Map((rows || []).map((ch) => [normChannel(ch.name), ch]));
  return CHANNEL_DISPLAY_ORDER.map((name) => {
    const hit = byName.get(name);
    if (hit) return { ...hit, name };
    return { name, ...emptySides(keys) };
  });
};

const PLANNER_CALC_FAILED =
  "We couldn't update the plan. Your current numbers are unchanged — try Calculate again.";

const toUserFacingError = (raw) => {
  if (!raw || typeof raw !== "string") return PLANNER_CALC_FAILED;
  const s = raw.trim();
  if (/index |traceback|exception|axis \d|typeerror|valueerror|keyerror|indexerror/i.test(s)) {
    return PLANNER_CALC_FAILED;
  }
  if (s.length > 160) return PLANNER_CALC_FAILED;
  return s;
};

const fmtK = (v) => {
  if (Math.abs(v) >= 1e6) return "$" + (v / 1e6).toFixed(1) + "M";
  if (Math.abs(v) >= 1e3) return "$" + Math.round(v / 1e3).toLocaleString() + "K";
  return "$" + Math.round(v).toLocaleString();
};
const fmtPts = (v) => Math.round(v).toLocaleString();

const parseBudgetInput = (raw) => {
  const cleaned = String(raw).replace(/[,$]/g, "");
  if (cleaned === "") return 0;
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? null : Math.max(0, n);
};

const sumQuarterBudgets = (current, quarterList) =>
  quarterList.reduce((s, q) => s + (Number(current[q + "Budget"]) || 0), 0);

const scaleQuartersToTotal = (current, quarterList, newTotal) => {
  const next = { ...current };
  const oldTotal = sumQuarterBudgets(current, quarterList);
  if (oldTotal <= 0) {
    quarterList.forEach((q, i) => {
      next[q + "Budget"] = i === 0 ? newTotal : 0;
    });
  } else {
    const factor = newTotal / oldTotal;
    quarterList.forEach((q) => {
      next[q + "Budget"] = (Number(current[q + "Budget"]) || 0) * factor;
    });
  }
  next.totalBudget = newTotal;
  return next;
};

const EditableBudgetCell = ({ value, onChange, borderLeft, disabled }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");

  return (
    <td style={{
      padding: "10px 6px",
      textAlign: "center",
      ...(borderLeft ? { borderLeft } : {}),
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
        <span style={{ fontSize: 13, color: C.muted }}>$</span>
        <input
          type="text"
          disabled={disabled}
          value={editing ? text : Math.round(value || 0).toLocaleString()}
          onFocus={() => {
            if (disabled) return;
            setEditing(true);
            setText(String(Math.round(value || 0)));
          }}
          onChange={(e) => {
            const raw = e.target.value.replace(/[,$]/g, "");
            if (raw === "" || /^\d*\.?\d*$/.test(raw)) setText(raw);
          }}
          onBlur={() => {
            setEditing(false);
            if (disabled) return;
            const parsed = parseBudgetInput(text);
            if (parsed !== null) onChange(parsed);
          }}
          style={{
            width: 88,
            padding: "4px 6px",
            fontSize: 13,
            fontWeight: 600,
            color: C.fg,
            textAlign: "center",
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            background: disabled ? C.bg : C.surface,
            opacity: disabled ? 0.7 : 1,
          }}
        />
      </div>
    </td>
  );
};

const StatCard = ({ label, value, delta, accent, subtitle }) => (
  <div style={{ background: C.surface, padding: "16px 20px", borderTop: accent ? `2px solid ${C.blue}` : "none", minWidth: 0 }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: C.dim, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 32, fontWeight: 700, color: C.fg, lineHeight: 1 }}>{value}</div>
    {delta !== undefined && (
      <div style={{ fontSize: 13, color: delta >= 0 ? C.green : C.red, marginTop: 4 }}>
        {delta >= 0 ? "+" : ""}{delta.toFixed(1)}%
      </div>
    )}
    {subtitle && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{subtitle}</div>}
  </div>
);

const IncrementalBudgetCard = ({ value, onChange, disabled }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");

  return (
    <div style={{ background: C.surface, padding: "16px 20px", borderTop: `2px solid ${C.amber}`, minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.dim, marginBottom: 4 }}>Incremental budget</div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 24, fontWeight: 700, color: C.muted }}>$</span>
        <input
          type="text"
          disabled={disabled}
          value={editing ? text : Math.round(value || 0).toLocaleString()}
          onFocus={() => {
            if (disabled) return;
            setEditing(true);
            setText(String(Math.round(value || 0)));
          }}
          onChange={(e) => {
            const raw = e.target.value.replace(/[,$]/g, "");
            if (raw === "" || /^\d*\.?\d*$/.test(raw)) setText(raw);
          }}
          onBlur={() => {
            setEditing(false);
            if (disabled) return;
            const parsed = parseBudgetInput(text);
            if (parsed !== null) onChange(parsed);
          }}
          style={{
            width: "100%",
            padding: "4px 6px",
            fontSize: 28,
            fontWeight: 700,
            color: C.fg,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            background: disabled ? C.bg : C.surface,
            opacity: disabled ? 0.7 : 1,
          }}
        />
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Extra $ for optimal envelope</div>
    </div>
  );
};

export default function QuarterlyBudgetPlanner() {
  const [data, setData] = useState([]);
  const [quartersMeta, setQuartersMeta] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("Global");
  const { setContext: setAskAnalystContext } = useAskAnalyst();

  useEffect(() => {
    setAskAnalystContext({
      region: selectedRegion === 'Global' ? 'GLOBAL' : selectedRegion,
      quarter: 'Q4FY26',
      activeTab: 'budget-planner',
    });
  }, [selectedRegion, setAskAnalystContext]);

  const [view, setView] = useState("current");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcError, setRecalcError] = useState(null);
  const [hasPendingEdits, setHasPendingEdits] = useState(false);
  const [incBudget, setIncBudget] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const recalcGenRef = useRef(0);
  const dataRef = useRef([]);
  const incBudgetRef = useRef(0);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    incBudgetRef.current = incBudget;
  }, [incBudget]);

  // Load plan data (current + optimal) from API
  useEffect(() => {
    recalcGenRef.current += 1;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setRecalcError(null);
      setHasPendingEdits(false);
      setIncBudget(0);
      try {
        const url = `${API_URL}/api/multi-quarter-plan?region=${encodeURIComponent(selectedRegion)}`;
        const res = await authenticatedFetch(url);
        if (cancelled) return;
        if (res.error) { setError(toUserFacingError(res.error)); return; }
        const qKeys = (res.quarters || []).map((q) => q.key);
        const channels = ensureAllChannels(res.channels || [], qKeys);
        setData(channels);
        setQuartersMeta(res.quarters || []);
        setView("current");
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRegion]);

  const quarters = useMemo(() => quartersMeta.map((q) => q.key), [quartersMeta]);
  const qLabels = useMemo(
    () => Object.fromEntries(quartersMeta.map((q) => [q.key, q.label])),
    [quartersMeta]
  );
  const qBudgetKey = (q) => q + "Budget";
  const qPtsKey = (q) => q + "Pts";

  const recalculatePlan = useCallback(async (snapshot, incremental) => {
    const gen = ++recalcGenRef.current;
    setRecalculating(true);
    setRecalcError(null);
    try {
      const currentTotal = snapshot.reduce(
        (s, ch) => s + (Number(ch.current?.totalBudget) || 0),
        0
      );
      const totalBudget = currentTotal + (Number(incremental) || 0);
      const res = await authenticatedFetch(`${API_URL}/api/optimize-multi-quarter`, {
        method: "POST",
        body: JSON.stringify({
          region: selectedRegion,
          total_budget: totalBudget,
          channels: snapshot.map((ch) => ({ name: ch.name, current: ch.current })),
        }),
      });
      if (gen !== recalcGenRef.current) return;
      if (res.error) {
        setRecalcError(PLANNER_CALC_FAILED);
        return;
      }
      const meta = res.quarters || [];
      const qKeys = meta.length ? meta.map((q) => q.key) : quarters;
      if (meta.length) setQuartersMeta(meta);
      setData(ensureAllChannels(res.channels || [], qKeys));
      setHasPendingEdits(false);
    } catch (e) {
      if (gen !== recalcGenRef.current) return;
      setRecalcError(PLANNER_CALC_FAILED);
    } finally {
      if (gen === recalcGenRef.current) setRecalculating(false);
    }
  }, [selectedRegion, quarters]);

  const handleCalculate = useCallback(() => {
    recalculatePlan(dataRef.current, incBudgetRef.current);
  }, [recalculatePlan]);

  const handleIncBudgetChange = useCallback((dollars) => {
    setIncBudget(Math.max(0, Number(dollars) || 0));
    setHasPendingEdits(true);
  }, []);

  const updateCurrentBudget = useCallback((channelIndex, field, dollars) => {
    const amount = Math.max(0, Number(dollars) || 0);
    setHasPendingEdits(true);
    setData((prev) =>
      prev.map((ch, i) => {
        if (i !== channelIndex) return ch;
        let nextCurrent = { ...ch.current };
        if (field === "totalBudget") {
          nextCurrent = scaleQuartersToTotal(nextCurrent, quarters, amount);
        } else {
          nextCurrent[field] = amount;
          nextCurrent.totalBudget = sumQuarterBudgets(nextCurrent, quarters);
        }
        return { ...ch, current: nextCurrent };
      })
    );
  }, [quarters]);

  const totals = useMemo(() => {
    const cur = { total: 0, pts: 0 };
    const opt = { total: 0, pts: 0 };
    data.forEach((ch) => {
      cur.total += ch.current.totalBudget; cur.pts += ch.current.totalPts;
      opt.total += ch.optimal.totalBudget; opt.pts += ch.optimal.totalPts;
    });
    return { cur, opt };
  }, [data]);

  const uplift = totals.cur.pts > 0 ? ((totals.opt.pts - totals.cur.pts) / totals.cur.pts) * 100 : 0;
  const quartersRangeLabel = quartersMeta.length
    ? `${quartersMeta[0].label}\u2013${quartersMeta[quartersMeta.length - 1].label}`
    : "";

  if (loading) {
    return (
      <div style={{ background: C.bg, minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 60 }}>
        <div style={{ color: C.muted, fontSize: 14 }}>Loading plan data for {selectedRegion}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: C.bg, minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 60 }}>
        <div style={{ color: C.red, fontSize: 14 }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, color: C.fg, minHeight: "100%", padding: 0 }}>
      {/* Header */}
      <div style={{ background: C.bg2, padding: "24px 32px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.dim, marginBottom: 4 }}>Optimal Budget Pacing Tool</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>Forward Quarter Planner</div>
          <div style={{ fontSize: 14, color: C.muted, marginTop: 4 }}>Compare current vs. optimal budget allocation across channels.</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            title="Learn about the business problem this solves"
            style={{
              padding: 8,
              border: "none",
              borderRadius: "50%",
              background: "transparent",
              color: C.accent,
              cursor: "pointer",
            }}
          >
            <Info size={22} />
          </button>
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            style={{ padding: "8px 32px 8px 12px", fontSize: 14, fontWeight: 500, border: `1px solid ${C.blue}`, borderRadius: 6, background: C.surface, color: C.fg, cursor: "pointer", appearance: "auto" }}
          >
            {REGIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ padding: "24px 32px" }}>
        {/* Stat cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12, marginBottom: 24 }}>
          <StatCard label={`Current budget (${quartersRangeLabel})`} value={fmtK(totals.cur.total)} accent />
          <IncrementalBudgetCard
            value={incBudget}
            disabled={recalculating}
            onChange={handleIncBudgetChange}
          />
          <StatCard label="Current plan pts" value={fmtPts(totals.cur.pts)} />
          <StatCard
            label="Optimal plan pts"
            value={fmtPts(totals.opt.pts)}
            delta={uplift}
          />
          <StatCard
            label={`Optimal budget (${quartersRangeLabel})`}
            value={fmtK(totals.opt.total)}
            subtitle={
              totals.opt.total !== totals.cur.total
                ? `Current plan: ${fmtK(totals.cur.total)}${incBudget > 0 && !hasPendingEdits ? ` + ${fmtK(incBudget)} incremental` : ""}`
                : "Same total as current"
            }
          />
        </div>

        {hasPendingEdits && !recalculating && !recalcError && (
          <div style={{
            marginBottom: 12,
            padding: "10px 14px",
            borderRadius: 6,
            fontSize: 13,
            background: C.bg2,
            color: C.muted,
            border: `1px solid ${C.border}`,
          }}>
            Budget edits not applied — click Calculate to update PTs and optimal allocation.
          </div>
        )}
        {(recalculating || recalcError) && (
          <div style={{
            marginBottom: 12,
            padding: "10px 14px",
            borderRadius: 6,
            fontSize: 13,
            background: recalcError ? "#FDECEC" : C.bg2,
            color: recalcError ? C.red : C.muted,
            border: `1px solid ${recalcError ? C.red : C.border}`,
          }}>
            {recalcError || "Calculating PT counts and optimal allocation…"}
          </div>
        )}

        {/* View toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          {[
            { key: "current", label: "Current Plan", color: C.blue },
            { key: "optimal", label: "Optimal Allocation", color: C.green },
            { key: "compare", label: "Compare", color: C.amber },
          ].map((v) => (
            <button key={v.key} onClick={() => setView(v.key)} style={{
              fontSize: 13, fontWeight: 600, padding: "8px 16px", borderRadius: 6, cursor: "pointer",
              border: `1px solid ${view === v.key ? v.color : C.border2}`,
              background: view === v.key ? v.color + "18" : "transparent",
              color: view === v.key ? v.color : C.muted,
            }}>
              {v.label}
            </button>
          ))}
          <button
            onClick={handleCalculate}
            disabled={recalculating || !hasPendingEdits}
            style={{
              marginLeft: "auto",
              fontSize: 13,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: 6,
              cursor: recalculating || !hasPendingEdits ? "not-allowed" : "pointer",
              border: `1px solid ${hasPendingEdits && !recalculating ? C.blue : C.border2}`,
              background: hasPendingEdits && !recalculating ? C.blue + "18" : "transparent",
              color: hasPendingEdits && !recalculating ? C.blue : C.muted,
              opacity: recalculating || !hasPendingEdits ? 0.7 : 1,
            }}
          >
            {recalculating ? "Calculating…" : "Calculate"}
          </button>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          {(() => {
            const sep = `1px solid ${C.border}`;
            const isC = view === "compare";
            return (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              {isC ? (
                <>
                  <tr style={{ borderBottom: `1px solid ${C.border2}` }}>
                    <th rowSpan={2} style={{ textAlign: "left", padding: "10px 6px", fontSize: 13, fontWeight: 600, color: C.dim, verticalAlign: "bottom" }}>Channel</th>
                    <th colSpan={4} style={{ textAlign: "center", padding: "8px 6px", fontSize: 13, fontWeight: 600, color: C.dim, borderBottom: `1px dotted ${C.border2}`, borderLeft: sep }}>Total</th>
                    {quarters.map((q) => (
                      <th key={q} colSpan={4} style={{ textAlign: "center", padding: "8px 6px", fontSize: 13, fontWeight: 600, color: C.dim, borderBottom: `1px dotted ${C.border2}`, borderLeft: sep }}>{qLabels[q]}</th>
                    ))}
                    <th rowSpan={2} style={{ textAlign: "center", padding: "10px 6px", fontSize: 13, fontWeight: 600, color: C.dim, verticalAlign: "bottom", borderLeft: sep }}>Pt Delta</th>
                  </tr>
                  <tr style={{ borderBottom: `1px dotted ${C.border2}` }}>
                    <th style={{ textAlign: "center", padding: "6px 6px", fontSize: 11, fontWeight: 600, color: C.blue, borderLeft: sep }}>Cur $</th>
                    <th style={{ textAlign: "center", padding: "6px 6px", fontSize: 11, fontWeight: 600, color: C.blue }}>Cur Pts</th>
                    <th style={{ textAlign: "center", padding: "6px 6px", fontSize: 11, fontWeight: 600, color: C.accent }}>Opt $</th>
                    <th style={{ textAlign: "center", padding: "6px 6px", fontSize: 11, fontWeight: 600, color: C.accent }}>Opt Pts</th>
                    {quarters.map((q) => (
                      <React.Fragment key={q + "sub"}>
                        <th style={{ textAlign: "center", padding: "6px 6px", fontSize: 11, fontWeight: 600, color: C.blue, borderLeft: sep }}>Cur $</th>
                        <th style={{ textAlign: "center", padding: "6px 6px", fontSize: 11, fontWeight: 600, color: C.blue }}>Cur Pts</th>
                        <th style={{ textAlign: "center", padding: "6px 6px", fontSize: 11, fontWeight: 600, color: C.accent }}>Opt $</th>
                        <th style={{ textAlign: "center", padding: "6px 6px", fontSize: 11, fontWeight: 600, color: C.accent }}>Opt Pts</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </>
              ) : (
                <tr style={{ borderBottom: `1px dotted ${C.border2}` }}>
                  <th style={{ textAlign: "left", padding: "10px 6px", fontSize: 13, fontWeight: 600, color: C.dim }}>Channel</th>
                  <th style={{ textAlign: "center", padding: "10px 6px", fontSize: 13, fontWeight: 600, color: C.dim }}>
                    {view === "optimal" ? "Optimal Total $" : "Current Total $"}
                  </th>
                  <th style={{ textAlign: "center", padding: "10px 6px", fontSize: 13, fontWeight: 600, color: C.dim }}>
                    {view === "optimal" ? "Optimal Total Pts" : "Current Total Pts"}
                  </th>
                  {quarters.map((q) => (
                    <React.Fragment key={q + "hdr"}>
                      <th style={{ textAlign: "center", padding: "10px 6px", fontSize: 12, fontWeight: 600, color: C.dim, borderLeft: sep }}>
                        {qLabels[q]} $
                      </th>
                      <th style={{ textAlign: "center", padding: "10px 6px", fontSize: 12, fontWeight: 600, color: C.dim }}>
                        {qLabels[q]} Pts
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {data.map((ch, idx) => {
                const cur = ch.current;
                const opt = ch.optimal;
                const ptDelta = opt.totalPts - cur.totalPts;

                return (
                  <tr key={idx} style={{ borderBottom: `1px dotted ${C.border}` }}>
                    <td style={{ padding: "10px 6px", color: C.fg, fontWeight: 500, fontSize: 14 }}>{ch.name}</td>

                    {view === "current" && (
                      <>
                        <EditableBudgetCell
                          value={cur.totalBudget}
                          disabled={recalculating}
                          onChange={(v) => updateCurrentBudget(idx, "totalBudget", v)}
                        />
                        <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 700, color: C.fg }}>{fmtPts(cur.totalPts)}</td>
                      </>
                    )}
                    {view === "optimal" && (
                      <>
                        <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 600, color: C.accent }}>{fmtK(opt.totalBudget)}</td>
                        <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 700, color: C.accent }}>{fmtPts(opt.totalPts)}</td>
                      </>
                    )}
                    {isC && (
                      <>
                        <EditableBudgetCell
                          value={cur.totalBudget}
                          borderLeft={sep}
                          disabled={recalculating}
                          onChange={(v) => updateCurrentBudget(idx, "totalBudget", v)}
                        />
                        <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 700, color: C.fg }}>{fmtPts(cur.totalPts)}</td>
                        <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 600, color: C.accent }}>{fmtK(opt.totalBudget)}</td>
                        <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 700, color: C.accent }}>{fmtPts(opt.totalPts)}</td>
                      </>
                    )}

                    {quarters.map((q) => {
                      const curB = cur[qBudgetKey(q)];
                      const curP = cur[qPtsKey(q)];
                      const optB = opt[qBudgetKey(q)];
                      const optP = opt[qPtsKey(q)];
                      if (view === "current") {
                        return (
                          <React.Fragment key={q}>
                            <EditableBudgetCell
                              value={curB}
                              borderLeft={sep}
                              disabled={recalculating}
                              onChange={(v) => updateCurrentBudget(idx, qBudgetKey(q), v)}
                            />
                            <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 600, color: C.fg }}>{fmtPts(curP)}</td>
                          </React.Fragment>
                        );
                      }
                      if (view === "optimal") {
                        return (
                          <React.Fragment key={q}>
                            <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 600, color: C.accent, borderLeft: sep }}>{fmtK(optB)}</td>
                            <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 600, color: C.accent }}>{fmtPts(optP)}</td>
                          </React.Fragment>
                        );
                      }
                      return (
                        <React.Fragment key={q}>
                          <EditableBudgetCell
                            value={curB}
                            borderLeft={sep}
                            disabled={recalculating}
                            onChange={(v) => updateCurrentBudget(idx, qBudgetKey(q), v)}
                          />
                          <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 600, color: C.fg }}>{fmtPts(curP)}</td>
                          <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 600, color: C.accent }}>{fmtK(optB)}</td>
                          <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 600, color: C.accent }}>{fmtPts(optP)}</td>
                        </React.Fragment>
                      );
                    })}

                    {isC && (
                      <td style={{ padding: "10px 6px", textAlign: "center", fontWeight: 700, color: ptDelta >= 0 ? C.accent : C.red, borderLeft: sep }}>
                        {ptDelta >= 0 ? "+" : ""}{fmtPts(ptDelta)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `2px solid ${C.border2}` }}>
                <td style={{ padding: "12px 6px", fontWeight: 700, fontSize: 14, color: C.dim }}>Total</td>

                {view === "current" && (
                  <>
                    <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.fg }}>{fmtK(totals.cur.total)}</td>
                    <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, fontSize: 16, color: C.fg }}>{fmtPts(totals.cur.pts)}</td>
                  </>
                )}
                {view === "optimal" && (
                  <>
                    <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.accent }}>{fmtK(totals.opt.total)}</td>
                    <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, fontSize: 16, color: C.accent }}>{fmtPts(totals.opt.pts)}</td>
                  </>
                )}
                {isC && (
                  <>
                    <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.fg, borderLeft: sep }}>{fmtK(totals.cur.total)}</td>
                    <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, fontSize: 16, color: C.fg }}>{fmtPts(totals.cur.pts)}</td>
                    <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.accent }}>{fmtK(totals.opt.total)}</td>
                    <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, fontSize: 16, color: C.accent }}>{fmtPts(totals.opt.pts)}</td>
                  </>
                )}

                {quarters.map((q) => {
                  const curQ = data.reduce((s, ch) => s + ch.current[qBudgetKey(q)], 0);
                  const curQP = data.reduce((s, ch) => s + ch.current[qPtsKey(q)], 0);
                  const optQ = data.reduce((s, ch) => s + ch.optimal[qBudgetKey(q)], 0);
                  const optQP = data.reduce((s, ch) => s + ch.optimal[qPtsKey(q)], 0);
                  if (view === "current") {
                    return (
                      <React.Fragment key={q}>
                        <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.fg, borderLeft: sep }}>{fmtK(curQ)}</td>
                        <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.fg }}>{fmtPts(curQP)}</td>
                      </React.Fragment>
                    );
                  }
                  if (view === "optimal") {
                    return (
                      <React.Fragment key={q}>
                        <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.accent, borderLeft: sep }}>{fmtK(optQ)}</td>
                        <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.accent }}>{fmtPts(optQP)}</td>
                      </React.Fragment>
                    );
                  }
                  return (
                    <React.Fragment key={q}>
                      <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.fg, borderLeft: sep }}>{fmtK(curQ)}</td>
                      <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.fg }}>{fmtPts(curQP)}</td>
                      <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.accent }}>{fmtK(optQ)}</td>
                      <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, color: C.accent }}>{fmtPts(optQP)}</td>
                    </React.Fragment>
                  );
                })}

                {isC && (
                  <td style={{ padding: "12px 6px", textAlign: "center", fontWeight: 700, fontSize: 16, color: C.accent, borderLeft: sep }}>
                    +{fmtPts(totals.opt.pts - totals.cur.pts)}
                  </td>
                )}
              </tr>
            </tfoot>
          </table>
            );
          })()}
        </div>

        {/* How to read */}
        <div style={{ marginTop: 32, background: C.surface, padding: "20px 24px" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.dim, marginBottom: 12 }}>How to read this</div>
          <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5 }}>
            <p style={{ margin: "0 0 8px" }}><span style={{ color: C.blue }}>Current plan</span> — spend ($) and pts per quarter from the current plan CSV.</p>
            <p style={{ margin: "0 0 8px" }}><span style={{ color: C.green }}>Optimal allocation</span> — model-recommended spend and pts per quarter.</p>
            <p style={{ margin: "0 0 8px" }}><span style={{ color: C.amber }}>Compare</span> — edit Cur $ (same as Current Plan), then click Calculate to refresh Cur Pts, Opt $, and Opt Pts. Pt Delta is total pts gain.</p>
            <p style={{ margin: 0 }}><span style={{ color: C.amber }}>Incremental budget</span> — extra dollars added only to the optimal envelope (current total + incremental). Current $ stays unchanged.</p>
          </div>
        </div>
      </div>

      <InfoPopup
        isOpen={infoOpen}
        onClose={() => setInfoOpen(false)}
        title="Forward Quarter Planner - Business Problem"
        content={vizNarratives.quarterlyBudgetPlanner}
      />
    </div>
  );
}
